import React, { Fragment, useState } from 'react';
import { useTranslate } from 'react-admin';
import { useSelector, useDispatch } from 'react-redux';

import { VirtualizedList } from '~/components';
import { AuditLogType } from '~/types';
import { selectors } from '~/ducks/virtualizedList';
import { Column } from '~/components/VirtualizedList';
import {
  getVirtualizedListBegin,
  getVirtualizedListSuccess,
  getVirtualizedListFail,
} from '~/ducks/virtualizedList';
import { l } from './IdentityShow';
import { getAuditLog } from '~/api/auditLog';
import CustomCell from '~/resources/auditLog/CustomCell';
import ActionShow from '~/resources/auditLog/ActionShow';
import Filters from './components/Filters';
import { ApiIdentity, ApiIdentityFilters } from '~/types/apiIdentity';

export const resource = 'identityAuditLog';

interface Props {
  identityId?: string;
  record?: ApiIdentity;
}

const IdentityAuditLog = (props: Props) => {
  const { identityId, record } = props;

  const filters = useSelector(selectors.getFilters<ApiIdentityFilters>(resource));
  const list = useSelector(selectors.getList(resource));
  const { hasNextPage, isNextPageLoading, data } = list;

  const dispatch = useDispatch();

  const translate = useTranslate();
  const t = (key: string): string => translate(l(key));

  const columns: Column<AuditLogType>[] = [
    {
      selectCellData: (rowData) => rowData.timestamp,
      cellDataWrapperComponent: CustomCell,
      id: 'timestamp',
      header: t('timestamp'),
      style: {
        minWidth: 200,
        maxWidth: 220,
      },
    },
    {
      selectCellData: (rowData) => rowData.message,
      cellDataWrapperComponent: CustomCell,
      id: 'action',
      header: t('action'),
      style: {
        minWidth: 400,
      },
    },
  ];

  const loadNextPage = async () => {
    if (isNextPageLoading || (!list.cursors.next && data.length > 0)) return;
    const queryToken = Math.random();
    try {
      dispatch(getVirtualizedListBegin({ resource, queryToken }));

      const { data } = await getAuditLog({
        next: list.cursors.next,
        limit: 100,
        identityId,
        ...filters,
      });

      dispatch(getVirtualizedListSuccess({ resource, responseData: data, queryToken }));
    } catch (error) {
      const err = error as Error;
      dispatch(getVirtualizedListFail({ resource, error: err, queryToken }));
    }
  };

  const [showData, setShowData] = useState<AuditLogType | null>(null);
  const handleShowOpen = (data: AuditLogType) => setShowData(data);
  const handleShowClose = () => setShowData(null);

  return (
    <Fragment>
      <Filters record={record} />
      <VirtualizedList<AuditLogType>
        resource={resource}
        columns={columns}
        data={data}
        filters={filters}
        hasNextPage={hasNextPage}
        isNextPageLoading={isNextPageLoading}
        loadNextPage={loadNextPage}
        onRowClick={handleShowOpen}
      />
      <ActionShow showData={showData} onClose={handleShowClose} />
    </Fragment>
  );
};

export default IdentityAuditLog;
